import { FC, MouseEvent } from "react";
import { useForm } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";

import Button from "@/components/Button";
import { FormLayout, Input, Label, Larger } from "@/components/Forms";
import { Modal } from "@/components/Modal";
import SidePanel, {
  SidePanelBody,
  SidePanelContent,
  SidePanelFooter,
} from "@/components/SidePanel";
import { api } from "@/utils/api";
import useAlerts from "@/hooks/useAlerts";
import { handleFormErrors } from "@/utils/helpers";
import ValidationMessage from "@/components/Forms/ValidationMessage";
import { OptimisationProjectResource } from "@/types/resources";
import { OptimisationProjectNetworkEnum } from "@/utils/enums";

type FormDataT = {
  external_project_id: string;
  name: string;
  owner: string;
};

interface Props {
  onClose: () => void;
  optimisationProject: OptimisationProjectResource;
}

const EditOptimisationProjectForm: FC<Props> = ({
  onClose,
  optimisationProject,
}) => {
  const alerts = useAlerts();

  const queryClient = useQueryClient();

  const onCancel = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    onClose();
  };

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<FormDataT>({
    defaultValues: {
      external_project_id: optimisationProject.projectId,
      name: optimisationProject.name,
      owner: optimisationProject.owner,
    },
  });

  const onSubmit = handleSubmit(async (data) => {
    try {
      const { id } = await api.put<typeof data, { id: number }>(
        `/v1/optimisation-tests/${optimisationProject.id}`,
        data
      );

      await queryClient.invalidateQueries(
        ["optimisation_tests", { id: id.toString() }],
        {
          exact: true,
        }
      );

      onClose();

      alerts.success(`Optimisation test was successfully updated`);
    } catch (err) {
      handleFormErrors<FormDataT>(err, setError, alerts);
    }
  });

  return (
    <Modal>
      <SidePanel heading="New optimisation test" onClose={onClose}>
        <SidePanelBody onSubmit={onSubmit} autoComplete="off">
          <SidePanelContent>
            <FormLayout>
              <Larger>
                <Label htmlFor="idInput">Project ID</Label>
                <Input
                  {...register("external_project_id")}
                  id="idInput"
                  type="text"
                  placeholder="Add project ID"
                />
              </Larger>
              <Larger>
                <Label htmlFor="nameInput">Project name</Label>
                <Input
                  $hasError={!!errors.name}
                  id="nameInput"
                  type="text"
                  placeholder="Add project name"
                  {...register("name")}
                />
                {!!errors.name && (
                  <ValidationMessage>{errors.name.message}</ValidationMessage>
                )}
              </Larger>
              <Larger>
                <Label htmlFor="ownerInput">Owner / Director</Label>
                <Input
                  {...register("owner")}
                  $hasError={!!errors.owner}
                  id="ownerInput"
                  type="text"
                  placeholder="Add owner name"
                />
                {!!errors.owner && (
                  <ValidationMessage>{errors.owner.message}</ValidationMessage>
                )}
              </Larger>
              <Larger onClick={(e) => e.stopPropagation()}>
                <Label>Network</Label>
                <Input
                  disabled
                  readOnly
                  value={
                    optimisationProject.network ===
                    OptimisationProjectNetworkEnum.FACEBOOK
                      ? "Facebook"
                      : "Youtube"
                  }
                />
              </Larger>
            </FormLayout>
          </SidePanelContent>
          <SidePanelFooter $actions>
            <Button disabled={isSubmitting} loading={isSubmitting}>
              Update
            </Button>
            <Button type="button" color="secondary" onClick={onCancel}>
              Cancel
            </Button>
          </SidePanelFooter>
        </SidePanelBody>
      </SidePanel>
    </Modal>
  );
};

export default EditOptimisationProjectForm;
